'use client';
import { useEffect, useRef } from 'react';
import { useToast, type ToastAction } from './Toast';

export interface ModalReportItem {
  key: string;
  message: string;
  /** Defaults to an error; a confirmation the dialog's own request earned is `'ok'`. */
  tone?: 'ok' | 'error';
  /** Recoveries of the card; they act only while the dialog that rendered this is the active surface. */
  actions?: readonly ToastAction[];
}

/** Raises one card from INSIDE a dialog, so it belongs to that dialog's toast scope: it shows in the
 *  top surface, its actions work there, and it ends with the dialog. A component that owns the dialog calls
 *  `useToast` above its `<Modal>`, which binds the card to the page behind. Pass a new item to report again;
 *  `null` ends the card. Renders nothing. */
export function ModalReport({ report }: { report: ModalReportItem | null }) {
  const { toast, dismissKey } = useToast();
  const shown = useRef<string | null>(null);
  useEffect(() => {
    if (shown.current && shown.current !== report?.key) { dismissKey(shown.current); shown.current = null; }
    if (!report) return;
    shown.current = report.key;
    toast(report.message, report.tone ?? 'error', { key: report.key, ...(report.actions ? { actions: report.actions } : {}) });
  }, [report, toast, dismissKey]);
  return null;
}
